// VÝROBA PARTIÍ — mesačný podklad k súpisu pri zákazke BEZ VÝMER (Mirko 29. 9. 2026, ZŠ a MŠ Lomnička Z260105).
// „Tu nemáme výmeru vypočítanú ani nebude, ale mesačne dostaneme, ktorá partia koľko vyrobila; potrebujem ručné
// čerpania — zadáme manuálne koľko — a výplaty zadáme manuálne komu a koľko. Do budúcna sa to môže opakovať.“
// Office zadá každý mesiac, koľko ktorá partia vyrobila (hlásenie od technika alebo majiteľa). Z jedného potvrdenia
// vznikne súpis mesiaca pre objednávateľa (Σ za položku výkazu) aj nárok partie v zošite Mzdy 2026.
// Prekročenie výkazu sa iba ukáže, nič neblokuje (Mirko: „prekročenie vidíme“). Vlastný riadok mimo výkazu je
// voliteľný — „ak by niečo vyskočilo“. Výplaty komu a koľko ostávajú v Mzdách 2026 (Vyplatiť / Výplatný deň).
import React, { useEffect, useMemo, useRef, useState } from 'react'
import FormularV3 from './FormularV3.jsx'
import { COLORS, TYPO, SPACE, RADIUS } from '../theme.js'

const H = () => ({ Authorization: `Bearer ${localStorage.getItem('kamir_token') || ''}` })
const HJ = () => ({ ...H(), 'Content-Type': 'application/json' })
const num = (v) => { const n = parseFloat(String(v == null ? '' : v).replace(/\s/g, '').replace(',', '.')); return isFinite(n) ? n : 0 }
const f2 = (n, d = 2) => (Number(n) || 0).toLocaleString('sk-SK', { minimumFractionDigits: d, maximumFractionDigits: d })
const ymLabel = (ym) => { const [y, m] = String(ym || '').split('-'); return m && y ? `${m}/${y}` : ym }

const bunka = { padding: '6px 8px', borderTop: `1px solid ${COLORS.line}`, fontSize: TYPO.sizes.sm }
const hlav = { padding: '7px 8px', background: COLORS.bgSoft, fontSize: 11, fontWeight: 800, color: COLORS.muted, textTransform: 'uppercase', letterSpacing: '.04em', textAlign: 'left' }
const cislo = { width: 92, padding: '5px 7px', textAlign: 'right', fontFamily: TYPO.mono, fontSize: TYPO.sizes.sm, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm }

function Cislo({ value, onChange, placeholder = '' }) {   // voľné písanie, commit na blur (rovnako ako v Čerpaní)
  const [edit, setEdit] = useState(null)
  const shown = edit != null ? edit : (value == null || Number(value) === 0 ? '' : String(value).replace('.', ','))
  return <input value={shown} placeholder={placeholder} inputMode="decimal" autoComplete="off" style={cislo}
    onFocus={e => { setEdit(shown); const t = e.target; setTimeout(() => { try { t.select() } catch (_) {} }, 0) }}
    onChange={e => setEdit(e.target.value)}
    onBlur={e => { onChange(num(e.target.value)); setEdit(null) }}   // hodnota z poľa, nie zo stavu — rýchly zápis sa nestratí
    onKeyDown={e => { if (e.key === 'Enter') e.target.blur() }} />
}

// ciele = { budget_item_id: mesačné množstvo z Čerpania } — office zaškrtla riadky a rozdeľuje ich medzi partie
// (Mirko 30. 9. 2026: „office zoberie mesačnú výmeru a rozdelí ju partiám omietkárov, ale na faktúru idú spolu“).
// Okno vtedy ukazuje, koľko ešte zostáva rozdeliť, a upozorní, keď súčet po partiách nesedí s mesačnou výmerou.
// navyse_v5 (Mirko 30. 9. 2026: „prečo neviem pridať vlastný riadok z výkazu partií?“) — vlastná línia z Čerpania
// chodí zo servera v tom istom zozname ako položky výkazu (budget_item_id = jej kľúč 'nav|…'), takže je to normálny
// stĺpec mriežky a rozdelí sa partiám ako všetko ostatné. Preto už netreba predvyplnené riadky v bloku „Navyše“ —
// boli by zapísané dvakrát. Blok „Navyše, mimo výkazu“ ostáva na to, čo v súpise ešte vôbec nie je.
export default function VyrobaPartii({ projectId, ym, onZavri, onHotovo, ciele = null }) {
  const [d, setD] = useState(null)
  const [chyba, setChyba] = useState('')
  const [mriezka, setMriezka] = useState({})        // `${partia_id}:${budget_item_id}` → { vymera, sadzba }
  const [riadkyPartii, setRiadkyPartii] = useState([])   // id partií v mriežke (poradie)
  const [vlastne, setVlastne] = useState([])        // voliteľné riadky mimo výkazu (to, čo v súpise ešte nie je)
  const [suhrn, setSuhrn] = useState(null)
  const [pocita, setPocita] = useState(false)

  useEffect(() => {
    let zive = true
    fetch(`/api/supis-vyroba/${projectId}/${ym}`, { headers: H() })
      .then(r => r.json().then(j => ({ ok: r.ok, j })))
      .then(({ ok, j }) => {
        if (!zive) return
        if (!ok) { setChyba(j.error || 'Načítanie zlyhalo.'); setD({ vv: [], partie: [] }); return }
        setD(j)
        const m = {}; const poradie = []; const vl = []
        ;(j.zapisane || []).forEach(r => {
          if (r.budget_item_id) {
            m[r.partia_id + ':' + r.budget_item_id] = { vymera: num(r.vymera), sadzba: num(r.sadzba) }
          } else {
            // Starší zápis (pred navyse_v5) nemá kľúč. Ak jeho línia dnes existuje ako stĺpec, patrí DO STĹPCA —
            // inak by tá istá práca stála aj v mriežke aj v bloku „Navyše" a zapísala by sa dvakrát.
            const linia = (j.vv || []).find(v => v.vlastny
              && String(v.popis || '').trim() === String(r.popis || '').trim()
              && (!r.mj || String(v.mj || '') === String(r.mj || '')))
            if (linia) {
              m[r.partia_id + ':' + linia.budget_item_id] = { vymera: num(r.vymera), sadzba: num(r.sadzba), sadzbaRucne: num(r.sadzba) >= 0 }
            } else {
              vl.push({ _id: 'z' + r.partia_id + ':' + String(r.popis || '').slice(0, 24), partia_id: r.partia_id, popis: r.popis || '', mj: r.mj || 'm²', cena_mj: 0, vymera: num(r.vymera), sadzba: num(r.sadzba) })
            }
          }
          if (!poradie.includes(r.partia_id)) poradie.push(r.partia_id)
        })
        if (!poradie.length) (j.partie || []).filter(p => p.navrhnuta).forEach(p => poradie.push(p.id))
        setMriezka(m); setRiadkyPartii(poradie); setVlastne(vl)
      })
      .catch(e => { if (zive) { setChyba(e.message); setD({ vv: [], partie: [] }) } })
    return () => { zive = false }
  }, [projectId, ym])

  const vvAll = (d && d.vv) || []
  const vv = ciele ? vvAll.filter(v => v.budget_item_id in ciele) : vvAll   // pri rozdeľovaní ukáž len zaškrtnuté položky
  const rozdelene = (biId) => riadkyPartii.reduce((a, pid) => a + num((mriezka[pid + ':' + biId] || {}).vymera), 0)
  const zostava = (biId) => ciele ? Math.round((num(ciele[biId]) - rozdelene(biId)) * 10000) / 10000 : 0
  const partieAll = (d && d.partie) || []
  const menoPartie = (id) => (partieAll.find(p => p.id === id) || {}).nazov || ('partia ' + id)

  const dajRiadky = () => {
    const out = []
    riadkyPartii.forEach(pid => vv.forEach(v => {
      const c = mriezka[pid + ':' + v.budget_item_id]
      // posiela sa sadzba, ktorú okno UKAZUJE (vrátane predvyplnenej) — inak by nárok vyšiel 0 €;
      // ak ju office prepísala rukou, platí jej číslo, aj keď je nula
      if (c && num(c.vymera) > 0) out.push({ partia_id: pid, budget_item_id: v.budget_item_id, vymera: num(c.vymera), sadzba: c.sadzbaRucne ? num(c.sadzba) : sadzbaPre(pid, v.budget_item_id) })
    }))
    vlastne.forEach(r => { if (num(r.vymera) > 0 && String(r.popis || '').trim() && num(r.partia_id) > 0) out.push({ partia_id: num(r.partia_id), popis: r.popis, mj: r.mj || 'm²', cena_mj: num(r.cena_mj), vymera: num(r.vymera), sadzba: num(r.sadzba) }) })
    return out
  }

  // sadzbaRucne: keď ju office prepísala (aj na nulu), platí jej číslo — inak sa doplní posledná známa sadzba partie
  const nastav = (pid, biId, pole, val) => setMriezka(p => ({ ...p, [pid + ':' + biId]: { ...(p[pid + ':' + biId] || { vymera: 0, sadzba: 0 }), [pole]: val, ...(pole === 'sadzba' ? { sadzbaRucne: true } : {}) } }))
  const sadzbaPre = (pid, biId) => {
    const c = mriezka[pid + ':' + biId]
    if (c && c.sadzbaRucne) return num(c.sadzba)
    if (c && num(c.sadzba) > 0) return num(c.sadzba)
    return num((d && d.sadzby && d.sadzby[pid + ':' + biId]) || 0)   // predvyplní sa z posledného nároku tej partie
  }

  const pocitadlo = useRef(0)            // kľúče riadkov bloku „Navyše" — index by sa po zmazaní zopakoval
  const poslednyVypocet = useRef('')
  const prepocitajAkTreba = (hodnoty) => {   // volá sa pri prechode na súhrn; ten istý vstup sa druhýkrát nepočíta
    const kluc = JSON.stringify(dajRiadky()) + '|' + String(hodnoty.dovod || '')
    if (kluc === poslednyVypocet.current) return
    poslednyVypocet.current = kluc
    spocitaj(hodnoty)
  }
  const spocitaj = async (hodnoty) => {   // suchý beh na serveri — do súhrnu ide presne to, čo sa zapíše
    setPocita(true)
    try {
      const r = await fetch('/api/supis-vyroba', { method: 'POST', headers: HJ(), body: JSON.stringify({ projectId, ym, riadky: dajRiadky(), dovod: hodnoty.dovod || '', dry: true }) })
      const j = await r.json().catch(() => ({}))
      if (!r.ok) { setChyba(j.error || ('HTTP ' + r.status)); setSuhrn(null); return false }
      setChyba(''); setSuhrn(j); return true
    } catch (e) { setChyba(e.message); return false } finally { setPocita(false) }
  }

  const uloz = async (hodnoty) => {
    const r = await fetch('/api/supis-vyroba', { method: 'POST', headers: HJ(), body: JSON.stringify({ projectId, ym, riadky: dajRiadky(), dovod: hodnoty.dovod || '' }) })
    const j = await r.json().catch(() => ({}))
    if (!r.ok) { window.alert(j.error || ('Zápis zlyhal: HTTP ' + r.status)); return false }
    if (j.upozornenia && j.upozornenia.length) window.alert(j.upozornenia.join('\n'))
    if (onHotovo) await onHotovo(j)
    return true
  }

  // ── krok 1: mriežka partia × položka výkazu ──────────────────────────────────────────────────
  const krokVyroba = ({ hodnoty, set, chyby }) => (
    <div>
      {(chyba || (chyby && chyby._vyroba)) ? <div style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#b91c1c', borderRadius: RADIUS.md, padding: '8px 12px', marginBottom: SPACE.sm, fontSize: TYPO.sizes.sm }}>{chyba || chyby._vyroba}</div> : null}
      <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted, marginBottom: SPACE.sm }}>
        {ciele
          ? 'Rozdeľ mesačnú výmeru medzi partie, ktoré na tom robili. Na faktúru a do súpisu ide súčet, objednávateľ partie nevidí; každá partia dostane vlastný nárok v Mzdách 2026 podľa svojej sadzby.'
          : 'Zákazka nemá výmery — podkladom k súpisu je to, čo partie za mesiac vyrobili. Zapíš hlásené množstvá; súčet za položku ide do súpisu pre objednávateľa, množstvo krát sadzba do zošita Mzdy 2026.'}
      </div>
      <div style={{ overflowX: 'auto', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={{ ...hlav, minWidth: 150 }}>Partia</th>
            {vv.map(v => <th key={v.budget_item_id} style={{ ...hlav, textAlign: 'right', minWidth: 190 }}>{v.popis}{v.vlastny ? <span style={{ marginLeft: 6, fontSize: 10, fontWeight: 800, color: '#b45309' }}>navyše</span> : null}<br /><span style={{ textTransform: 'none', fontWeight: 600 }}>{v.mj} · {f2(v.cena_mj)} €/{v.mj}</span>
              {ciele ? <><br /><span style={{ textTransform: 'none', fontWeight: 700, color: Math.abs(zostava(v.budget_item_id)) < 0.005 ? COLORS.muted : '#b45309' }}>
                za mesiac {f2(num(ciele[v.budget_item_id]), 3)} · {Math.abs(zostava(v.budget_item_id)) < 0.005 ? 'rozdelené celé' : 'zostáva ' + f2(zostava(v.budget_item_id), 3)}</span></> : null}</th>)}
          </tr></thead>
          <tbody>
            {riadkyPartii.map(pid => (
              <tr key={pid}>
                <td style={{ ...bunka, fontWeight: 700 }}>
                  {menoPartie(pid)}
                  <div><button type="button" onClick={() => setRiadkyPartii(p => p.filter(x => x !== pid))} style={{ background: 'none', border: 'none', color: COLORS.muted, cursor: 'pointer', fontSize: 11, padding: 0 }}>odobrať</button></div>
                </td>
                {vv.map(v => (
                  <td key={v.budget_item_id} style={{ ...bunka, textAlign: 'right' }}>
                    <div style={{ display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'center' }}>
                      <Cislo value={(mriezka[pid + ':' + v.budget_item_id] || {}).vymera} onChange={x => nastav(pid, v.budget_item_id, 'vymera', x)} placeholder={'0 ' + v.mj} />
                      <Cislo value={sadzbaPre(pid, v.budget_item_id)} onChange={x => nastav(pid, v.budget_item_id, 'sadzba', x)} placeholder="€/MJ" />
                    </div>
                  </td>
                ))}
              </tr>
            ))}
            {!riadkyPartii.length ? <tr><td style={{ ...bunka, color: COLORS.muted }} colSpan={vv.length + 1}>Zatiaľ žiadna partia — vyber ju nižšie.</td></tr> : null}
          </tbody>
        </table>
      </div>
      <div style={{ display: 'flex', gap: SPACE.sm, alignItems: 'center', margin: `${SPACE.sm}px 0`, flexWrap: 'wrap' }}>
        <select value="" onChange={e => { const v = +e.target.value; if (v && !riadkyPartii.includes(v)) setRiadkyPartii(p => [...p, v]) }}
          style={{ padding: '6px 9px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm }}>
          <option value="">+ Pridať partiu…</option>
          {partieAll.filter(p => !riadkyPartii.includes(p.id)).map(p => <option key={p.id} value={p.id}>{p.nazov}{p.navrhnuta ? ' (robí tu)' : ''}</option>)}
        </select>
        <span style={{ fontSize: 11, color: COLORS.muted }}>Prvé číslo je množstvo, druhé sadzba partie za mernú jednotku. Sadzba sa nabudúce predvyplní.</span>
      </div>

      {/* voliteľný riadok mimo výkazu — Mirko: „nechaj aj možný voliteľný riadok, ak by niečo vyskočilo“ */}
      <div style={{ border: `1px dashed ${COLORS.line}`, borderRadius: RADIUS.md, padding: SPACE.sm, marginBottom: SPACE.sm }}>
        <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.muted, textTransform: 'uppercase', marginBottom: 6 }}>Navyše, mimo výkazu (ak niečo vyskočilo)</div>
        {vlastne.map((r, i) => (
          <div key={r._id || i} style={{ display: 'flex', gap: 6, alignItems: 'center', marginBottom: 6, flexWrap: 'wrap' }}>
            <select value={r.partia_id || ''} onChange={e => setVlastne(p => p.map((x, j) => j === i ? { ...x, partia_id: e.target.value ? +e.target.value : '' } : x))}
              style={{ padding: '5px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm }}>
              <option value="">partia…</option>
              {partieAll.map(p => <option key={p.id} value={p.id}>{p.nazov}</option>)}
            </select>
            <input value={r.popis} onChange={e => setVlastne(p => p.map((x, j) => j === i ? { ...x, popis: e.target.value } : x))} placeholder="popis práce"
              style={{ flex: 1, minWidth: 180, padding: '5px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm }} />
            <input value={r.mj} onChange={e => setVlastne(p => p.map((x, j) => j === i ? { ...x, mj: e.target.value } : x))} placeholder="MJ"
              style={{ width: 60, padding: '5px 8px', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, fontSize: TYPO.sizes.sm }} />
            <Cislo value={r.vymera} onChange={x => setVlastne(p => p.map((y, j) => j === i ? { ...y, vymera: x } : y))} placeholder="množstvo" />
            <Cislo value={r.cena_mj} onChange={x => setVlastne(p => p.map((y, j) => j === i ? { ...y, cena_mj: x } : y))} placeholder="€/MJ súpis" />
            <Cislo value={r.sadzba} onChange={x => setVlastne(p => p.map((y, j) => j === i ? { ...y, sadzba: x } : y))} placeholder="€/MJ partia" />
            <button type="button" onClick={() => setVlastne(p => p.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontWeight: 800 }}>✕</button>
          </div>
        ))}
        <button type="button" onClick={() => { pocitadlo.current += 1; setVlastne(p => [...p, { _id: 'n' + pocitadlo.current, partia_id: riadkyPartii[0] || '', popis: '', mj: 'm²', cena_mj: 0, vymera: 0, sadzba: 0 }]) }}
          style={{ background: '#fff', border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.sm, padding: '5px 10px', fontSize: TYPO.sizes.sm, cursor: 'pointer', fontWeight: 700 }}>+ Riadok navyše</button>
      </div>

      <label style={{ display: 'block', fontSize: TYPO.sizes.xs, fontWeight: 700, marginBottom: 3 }}>Odkiaľ je hlásenie<b style={{ color: '#b91c1c' }}> *</b></label>
      <input value={hodnoty.dovod || ''} onChange={e => set('dovod', e.target.value)} placeholder="napr. hlásenie technika Ruru z 25. 10. 2026"
        style={{ width: '100%', padding: '7px 10px', fontSize: TYPO.sizes.base, border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md }} />
      <div style={{ fontSize: TYPO.sizes.xs, color: COLORS.muted, marginTop: 3 }}>Kto a kedy nahlásil vyrobené množstvá (technik alebo majiteľ). Zostane v zázname zmien aj na riadkoch súpisu.</div>
    </div>
  )

  // ── krok 2: súhrn — čo presne vznikne (doráta sa na serveri pri otvorení kroku) ──────────────
  const krokSuhrn = () => {
    if (pocita || !suhrn) return <div style={{ padding: SPACE.md, color: COLORS.muted }}>{chyba || 'Počítam súhrn…'}</div>
    const s = suhrn
    return (
      <div>
        {s.znizenia && s.znizenia.length ? (
          <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: RADIUS.md, padding: '8px 12px', marginBottom: SPACE.sm, fontSize: TYPO.sizes.sm, color: '#b91c1c' }}>
            <b>Pozor, zníži sa množstvo v Čerpaní:</b> {s.znizenia.map(z => `${z.popis} — z ${f2(z.vymera === undefined ? z.bolo : z.bolo)} na ${f2(z.bude)} ${z.mj} (o ${f2(z.rozdiel)} menej)`).join(' · ')}.
            {' '}Partiám sa rozdelilo menej, než je v súpise. Ak to tak nemá byť, vráť sa a rozdeľ zvyšok.
          </div>
        ) : null}
        {s.prekrocenia && s.prekrocenia.length ? (
          <div style={{ background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: RADIUS.md, padding: '8px 12px', marginBottom: SPACE.sm, fontSize: TYPO.sizes.sm }}>
            <b>Nad výkaz výmer:</b> {s.prekrocenia.map(p => `${p.popis} — spolu ${f2(p.kumulativ)} z ${f2(p.zod)} ${p.mj} (o ${f2(p.nad)} viac)`).join(' · ')}. Zapísať sa dá, ale nad výkaz patrí zmenový príkaz.
          </div>
        ) : null}
        <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden', marginBottom: SPACE.sm }}>
          <div style={{ padding: '8px 12px', background: COLORS.bgSoft, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: COLORS.muted }}>Súpis {ymLabel(ym)} pre objednávateľa</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {s.supis.map((x, i) => (
                <tr key={i}>
                  <td style={bunka}>{x.popis}{x.vlastny ? <span style={{ color: COLORS.muted }}> (navyše)</span> : null}</td>
                  <td style={{ ...bunka, textAlign: 'right', fontFamily: TYPO.mono }}>{f2(x.vymera, 3)} {x.mj}</td>
                  <td style={{ ...bunka, textAlign: 'right', fontFamily: TYPO.mono }}>{f2(x.cena_mj)} €</td>
                  <td style={{ ...bunka, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 800 }}>{f2(x.celkom)} €</td>
                </tr>
              ))}
              <tr><td style={{ ...bunka, fontWeight: 800 }} colSpan={3}>Spolu za mesiac</td><td style={{ ...bunka, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 800 }}>{f2(s.supisSpolu)} €</td></tr>
            </tbody>
          </table>
        </div>
        <div style={{ border: `1px solid ${COLORS.line}`, borderRadius: RADIUS.md, overflow: 'hidden', marginBottom: SPACE.sm }}>
          <div style={{ padding: '8px 12px', background: COLORS.bgSoft, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', color: COLORS.muted }}>Mzdy 2026 — nárok partií</div>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <tbody>
              {s.partie.map(p => (
                <tr key={p.partia_id}>
                  <td style={bunka}>{p.nazov}</td>
                  <td style={{ ...bunka, textAlign: 'right', fontFamily: TYPO.mono }}>{f2(p.vymera, 3)}</td>
                  <td style={{ ...bunka, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 800 }}>{f2(p.suma)} €{p.bezSadzby ? <span style={{ color: '#b45309', fontWeight: 600 }}> · {p.bezSadzby}× bez sadzby</span> : null}</td>
                </tr>
              ))}
              <tr><td style={{ ...bunka, fontWeight: 800 }} colSpan={2}>Spolu nárok</td><td style={{ ...bunka, textAlign: 'right', fontFamily: TYPO.mono, fontWeight: 800 }}>{f2(s.mzdySpolu)} €</td></tr>
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: TYPO.sizes.sm, color: COLORS.muted }}>
          {s.nahradiRiadkov ? `Prepíše ${s.nahradiRiadkov} už uložených riadkov súpisu tohto mesiaca. ` : ''}
          Výplaty komu a koľko zadáš v Mzdách 2026 (Vyplatiť alebo Výplatný deň). Nič sa neuloží, kým nepotvrdíš.
        </div>
      </div>
    )
  }

  const kroky = useMemo(() => [
    { id: 'vyroba', nazov: (ciele ? 'Rozdelenie mesačnej výmery — ' : 'Výroba partií — ') + ymLabel(ym), tlacidlo: 'Súhrn →', vlastny: krokVyroba,
      over: (h) => {
        // neúplný riadok v bloku „Navyše" sa už NEZAHODÍ ticho — povedz presne, čo mu chýba (Mirko 30. 9. 2026)
        const chybne = []
        vlastne.forEach((r, i) => {
          const maCo = String(r.popis || '').trim() || num(r.vymera) > 0 || num(r.cena_mj) > 0
          if (!maCo) return   // úplne prázdny riadok nikomu neprekáža
          const chyba2 = []
          if (!num(r.partia_id)) chyba2.push('vyber partiu')
          if (!String(r.popis || '').trim()) chyba2.push('napíš popis')
          if (!(num(r.vymera) > 0)) chyba2.push('zadaj množstvo')
          if (chyba2.length) chybne.push('riadok navyše ' + (i + 1) + ': ' + chyba2.join(', '))
        })
        if (chybne.length) return { _vyroba: 'Doplň, čo chýba — ' + chybne.join(' · ') }
        if (!dajRiadky().length) return { _vyroba: 'Zadaj aspoň jedno množstvo — koľko ktorá partia vyrobila.' }
        if (!String(h.dovod || '').trim()) return { _vyroba: 'Napíš, odkiaľ je hlásenie (technik alebo majiteľ, dátum).' }
        if (ciele) {   // rozdelenie musí sedieť s mesačnou výmerou, inak by partia nemala kto zaplatiť (alebo by dostala viac)
          const zle = vv.filter(v => Math.abs(zostava(v.budget_item_id)) > 0.005)
          if (zle.length) return { _vyroba: 'Rozdelenie nesedí s mesačnou výmerou: ' + zle.map(v => v.popis + ' — ' + (zostava(v.budget_item_id) > 0 ? 'zostáva ' + f2(zostava(v.budget_item_id), 3) : 'rozdelené o ' + f2(-zostava(v.budget_item_id), 3) + ' viac')).join(' · ') }
        }
        prepocitajAkTreba(h)   // zadanie je v poriadku → priprav súhrn na ďalší krok
        return {}
      } },
    { id: 'suhrn', nazov: 'Súhrn — čo sa zapíše', tlacidlo: 'Zapísať', vlastny: krokSuhrn },
  ], [d, mriezka, riadkyPartii, vlastne, suhrn, pocita, chyba, ciele, ym, projectId])

  if (d === null) return null
  return (
    <FormularV3
      open titul={(ciele ? 'Rozdeliť partiám — ' : 'Výroba partií — ') + ymLabel(ym)} nazovZaznamu="výrobu partií" sirkaOkna="min(1100px, 97vw)"
      kroky={kroky} pociatocne={{ dovod: (d && d.dovod) || '' }}
      onUloz={uloz} onZavri={onZavri}
    />
  )
}
